// Permission utility functions for AESOP Pricing App

// Permission codes mapping (fully updated to match backend)
export const PERMISSION_CODES = {
  // Price Analysis
  PRICE_ANALYSIS_R : 'RAPP',
  PRICE_ANALYSIS_ML : 'PAML',
  // User Management (System | User Management)
  USER_MANAGEMENT: 'UMGN',
  USER_VIEW: 'UMVU',
  USER_ADD: 'UMAU',
  USER_EDIT: 'UMEU',
  USER_ACTIVE_INACTIVE: 'UMAI',
  // No explicit USER_DELETE in provided codes

  // Batch Pricing Override
  BATCH_PRICING_OVERRIDE: 'BPO',
  
  // Batch Pricing (JOB)
  BATCH_PRICING: 'RBP',

  // Sites (Master Data | Sites)
  SITE_MANAGEMENT: 'MDSL',
  SITE_VIEW: 'MSVS',
  SITE_ADD: 'MSAS',
  SITE_EDIT: 'MSES',
  SITE_DELETE: 'MSDS',
  SITE_ACTIVE_INACTIVE: 'MSAI',

  // Regions (Master Data | Regions)
  REGION_MANAGEMENT: 'MDRL',
  REGION_VIEW: 'MRVR',
  REGION_ADD: 'MRAR',
  REGION_EDIT: 'MRER',
  REGION_DELETE: 'MRDR',
  REGION_ACTIVE_INACTIVE: 'MRAI',

  // Roles & Permissions (System | Roles & Permissions)
  ROLE_MANAGEMENT: 'SRPL',
  ROLE_VIEW: 'RVRP',
  ROLE_ADD: 'RARP',
  ROLE_EDIT: 'RERP',
  ROLE_DELETE: 'PDRP',
  ROLE_ACTIVE_INACTIVE: 'RAIP',

  // Dashboard permissions
  DASHBOARD: 'DHBD',
  DASHBOARD_TODAY_SOLD_VALUE: 'DHBDTS',
  DASHBOARD_TODAY_PAID_VALUE: 'DHBDTP',
  DASHBOARD_TODAY_INVENTORY_COUNT: 'DHBDTI',
  DASHBOARD_PAYMENTS_BY_PRODUCT: 'DHBDPBP',
  DASHBOARD_DUE_PAST_45_DAYS: 'DHBDDPD',
  DASHBOARD_TOTAL_CRUSH_CORE_SALES: 'DHBDTCCS',
  DASHBOARD_AVAILABLE_INVENTORIES: 'DHBDAI',
  DASHBOARD_PRODUCTS_AVERAGE: 'DHBDPA',
  DASHBOARD_AVERAGE_SALES_PER_VEHICLE: 'DHBDASVD',

  // R Application
  //R_APPLICATION: 'RAPP',
  // Price Analysis R | Finalize
  PRICE_ANALYSIS_R_FINALIZE: 'PARFNLZ',
 
  All_Interchange:'AI',
  // Company (System | Company)
  COMPANY_MANAGEMENT: 'MDCL',
  COMPANY_VIEW: 'MCVC',
  COMPANY_ADD: 'MCAC',
  COMPANY_EDIT: 'MCEC',
  COMPANY_DELETE: 'MCDC',

  // System Config (Master Data | system Config)
  SYSTEM_CONFIG_PAGE: 'MDSC',
  SYSTEM_CONFIG_ADD: 'SCAS',
  SYSTEM_CONFIG_EDIT: 'SCES',
  SYSTEM_CONFIG_DELETE: 'SCDS',
  SYSTEM_CONFIG_VIEW: 'SCVS',

  // Reports
  IMPACT_ANALYSIS: 'RIA',
  Sufficiency_ANALYSIS: 'RSA',
  DEMAND_REPORT: 'RDR',
  REVENUE_REPORT: 'RR',
  PRICE_COMPARATIVE: 'RPC',
  GLOBLE_CONFIG : 'GC'

};

// Check if user has a specific permission
export const hasPermission = (user, permissionCode) => {
  if (!user || !user.permissions) return false;
  if (user.is_super_admin) return true;
  return user.permissions.some(permission => permission.permission_code === permissionCode);
};

// Check if user has any of the specified permissions
export const hasAnyPermission = (user, permissionCodes) => {
  if (!user || !user.permissions) return false;
  if (user.is_super_admin) return true;
  return permissionCodes.some(code => 
    user.permissions.some(permission => permission.permission_code === code)
  );
};

// Check if user has all of the specified permissions
export const hasAllPermissions = (user, permissionCodes) => {
  if (!user || !user.permissions) return false;
  if (user.is_super_admin) return true;
  return permissionCodes.every(code => 
    user.permissions.some(permission => permission.permission_code === code)
  );
};

// Get user's permission codes as an array
export const getUserPermissionCodes = (user) => {
  if (!user || !user.permissions) return [];
  return user.permissions.map(permission => permission.permission_code);
};

// Check if user has access to specific regions
export const hasRegionAccess = (user, regionId) => {
  if (!user) return false;
  if (user.is_super_admin) return true;
  if (user.regions && user.regions.length > 0) {
    return user.regions.some(region => region.regionID === regionId);
  }
  return true;
};

// Get user's accessible region IDs
export const getUserAccessibleRegionIds = (user) => {
  if (!user) return [];
  if (user.is_super_admin) return [];
  if (user.regions && user.regions.length > 0) {
    return user.regions.map(region => region.regionID);
  }
  return [];
};

// Check if user has access to specific sites
export const hasSiteAccess = (user, siteId) => {
  if (!user) return false;
  if (user.is_super_admin) return true;
  if (user.siteIDs && user.siteIDs.length > 0) {
    return user.siteIDs.includes(siteId);
  }
  if (user.regions && user.regions.length > 0) {
    return user.regions.some(region => 
      region.siteIDs && region.siteIDs.includes(siteId)
    );
  }
  return true;
};

// Get user's accessible site IDs
export const getUserAccessibleSiteIds = (user) => {
  if (!user) return [];
  if (user.is_super_admin) return [];
  if (user.siteIDs && user.siteIDs.length > 0) {
    return user.siteIDs;
  }
  if (user.regions && user.regions.length > 0) {
    const siteIds = [];
    user.regions.forEach(region => {
      if (region.siteIDs && region.siteIDs.length > 0) {
        siteIds.push(...region.siteIDs);
      }
    });
    return siteIds;
  }
  return [];
};

// Filter data based on user's region and site access
export const filterDataByUserAccess = (data, user, regionField = 'region_id', siteField = 'site_id') => {
  if (!user || !data) return data;
  if (user.is_super_admin) return data;

  const accessibleRegionIds = getUserAccessibleRegionIds(user);
  const accessibleSiteIds = getUserAccessibleSiteIds(user);

  return data.filter(item => {
    // Check region access
    if (regionField && item[regionField]) {
      if (accessibleRegionIds.length > 0 && !accessibleRegionIds.includes(item[regionField])) {
        return false;
      }
    }

    // Check site access
    if (siteField && item[siteField]) {
      if (accessibleSiteIds.length > 0 && !accessibleSiteIds.includes(item[siteField])) {
        return false;
      }
    }

    return true;
  });
};

export const getFirstPermissionRoute = (user) => {
  if (!user || !user.permissions) return '/dashboard';
  const companyPerms = ['MDCL', 'MCVC', 'MCAC', 'MCEC', 'MCDC'];
  if (user.permissions.some(p => companyPerms.includes(p.permission_code))) {
    return '/company';
  }
  // Add System Config as a possible first route
  const permToRoute = {
    DHBD: '/dashboard',
    UMGN: '/add-user', UMVU: '/add-user',
    SRPL: '/roles-and-permissions', RVRP: '/roles-and-permissions',
    MSVS: '/sites',
    MRVR: '/regions',
    RAPP: '/r-application',
    PARFNLZ: '/r-application',
    MDSC: '/system-config', SCVS: '/system-config', SCAS: '/system-config', SCES: '/system-config', SCDS: '/system-config',
    RIA: '/impact-analysis'
  };
  for (const p of user.permissions) {
    if (permToRoute[p.permission_code]) return permToRoute[p.permission_code];
  }
  return '/dashboard';
};

export function hasSystemConfigPermission(user, action) {
  if (!user || !user.permissions) return false;
  return user.permissions.includes(PERMISSION_CODES[action]);
}

export function hasPriceAnalysisRFinalizePermission(user) {
  if (!user || !user.permissions) return false;
  if (user.is_super_admin) return true;
  return user.permissions.some(permission => permission.permission_code === PERMISSION_CODES.PRICE_ANALYSIS_R_FINALIZE);
} 